import { useState, useEffect } from "react";
import { useAuth, API, usePush } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { User, Mail, Shield, Calendar, LogOut, Bell, BellOff } from "lucide-react";

const Profile = () => {
  const { user, logout } = useAuth();
  const pushNotifications = usePush();
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    deadlines: true,
    new_content: true,
    live_sessions: true,
    reminders: true
  });

  useEffect(() => {
    // Load push notification preferences
    if (pushNotifications?.status?.isSubscribed) {
      // Could fetch preferences from backend here
    }
  }, [pushNotifications]);

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
      toast.success("Logged out successfully");
      window.location.href = "/";
    } catch (error) {
      toast.error("Failed to logout");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in max-w-2xl mx-auto" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Profile
          </h1>
          <p className="text-stone-600 mt-1">Manage your account settings</p>
        </div>

        {/* Profile Card */}
        <Card className="bg-white rounded-xl border border-stone-100 overflow-hidden">
          <div className="h-24 bg-gradient-to-r from-violet-600 to-violet-800"></div>
          <CardContent className="pt-0 -mt-12 px-6 pb-6">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <Avatar className="h-24 w-24 ring-4 ring-white">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-violet-100 text-violet-700 text-2xl">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                  {user?.name}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`capitalize ${
                    user?.role === 'admin' 
                      ? 'bg-violet-100 text-violet-700' 
                      : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    <Shield className="h-3 w-3 mr-1" />
                    {user?.role}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <User className="h-5 w-5 text-violet-700" />
              </div>
              <div>
                <Label className="text-sm text-stone-500">Full Name</Label>
                <p className="font-medium text-stone-900">{user?.name}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <Mail className="h-5 w-5 text-violet-700" />
              </div>
              <div>
                <Label className="text-sm text-stone-500">Email Address</Label>
                <p className="font-medium text-stone-900">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-violet-700" />
              </div>
              <div>
                <Label className="text-sm text-stone-500">Member Since</Label>
                <p className="font-medium text-stone-900">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }} className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pushNotifications?.status?.isSubscribed ? (
              <>
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Push Notifications Enabled</p>
                      <p className="text-sm text-green-600">You'll receive browser notifications</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      const success = await pushNotifications?.unsubscribe();
                      if (success) {
                        toast.success("Unsubscribed from push notifications");
                      }
                    }}
                    data-testid="unsubscribe-push-btn"
                  >
                    <BellOff className="h-4 w-4 mr-2" />
                    Unsubscribe
                  </Button>
                </div>
                <p className="text-xs text-stone-500">
                  You can re-enable notifications anytime. Your consent was recorded for UK GDPR compliance.
                </p>
              </>
            ) : (
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <BellOff className="h-5 w-5 text-stone-400" />
                  <div>
                    <p className="font-medium text-stone-700">Push Notifications Disabled</p>
                    <p className="text-sm text-stone-500">
                      {pushNotifications?.status?.consentDenied 
                        ? "You've opted out of notifications" 
                        : "Complete a lesson to enable notifications"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Account Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              variant="destructive"
              className="w-full md:w-auto rounded-xl"
              onClick={handleLogout}
              disabled={loading}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {loading ? "Logging out..." : "Logout"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Profile;
